import { useState, type ReactNode } from 'react';
import { useT } from '../i18n/index.ts';

/**
 * Collapsed technical-detail card — Field Notes rebuild (epic #950
 * PR-8, formerly the console-shared `createTechnicalDetails` factory
 * instantiation). Raw ids and troubleshooting fields hide behind the
 * toggle; `${testId}-toggle` / `${testId}-content` are part of the test
 * contract.
 */
interface Props {
  readonly id?: string;
  readonly summary: string;
  readonly children: ReactNode;
  readonly testId: string;
  readonly compact?: boolean;
  readonly title?: string;
}

export function TechnicalDetails({
  id,
  summary,
  children,
  testId,
  compact = false,
  title,
}: Props): JSX.Element {
  const t = useT();
  const [open, setOpen] = useState(false);

  return (
    <section
      id={id}
      className={['rounded-lg border border-line-soft bg-raised', compact ? 'p-3' : 'p-4'].join(' ')}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h3 className="m-0 font-mono text-mono-label uppercase text-ink-mid">
            {title ?? t('common.technical_details.title')}
          </h3>
          <p
            className={[
              'mt-1 text-ink-low',
              compact ? 'text-mono-label leading-5' : 'text-caption leading-5',
            ].join(' ')}
          >
            {summary}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen((current) => !current)}
          className="inline-flex shrink-0 items-center justify-center rounded border border-line bg-card px-3 py-1.5 font-mono text-mono-label uppercase text-ink-mid transition-colors duration-150 hover:border-line-strong hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
          data-testid={`${testId}-toggle`}
        >
          {open ? t('common.technical_details.hide') : t('common.technical_details.show')}
        </button>
      </div>
      {open ? (
        <div
          className={[
            'mt-4 border-t border-line-soft pt-4',
            compact ? 'text-caption' : 'text-body',
          ].join(' ')}
          data-testid={`${testId}-content`}
        >
          {children}
        </div>
      ) : null}
    </section>
  );
}
